import React, {
  memo, useEffect, useState, useRef,
} from 'react';
import PropTypes from 'prop-types'; // Import PropTypes
import Routing from 'containers/Routing';
import { CombinedProvider, useDispatch, useStateValue } from 'state/contextState';
import mainReducer, { initialState } from 'reducers/mainReducer';
import { lf } from 'storage/localForage';
import { SET_INITIAL_STATE } from 'reducers/homePageReducer';

// https://reactjs.org/docs/optimizing-performance.html
// https://dev.to/changoman/react-optimizations-with-reactmemo-usecallback-and-usereducer-42ni
// https://overreacted.io/making-setinterval-declarative-with-react-hooks/
// https://stackoverflow.com/questions/5927284/how-can-i-make-setinterval-also-work-when-a-tab-is-inactive-in-chrome
// https://levelup.gitconnected.com/using-the-indexeddb-api-with-react-and-hooks-4e63d83a5d1b
// https://hackerfall.com/story/localforage--async-local-storage
// https://material.io/tools/icons/?style=baseline
// https://medium.com/simply/state-management-with-react-hooks-and-context-api-at-10-lines-of-code-baf6be8302c
// https://usehooks.com/
// https://codesandbox.io/s/nww9q0v67p?from-embed

// Moved loadState function outside the component
const createLoadState = (dispatch, setLoading) => {
  const loadState = (activeRef) => { // Removed async
    lf.getItem('homePageState')
      .then((savedHomePageState) => {
        if (activeRef.current && savedHomePageState) { // Check active flag before dispatch
          dispatch({ type: SET_INITIAL_STATE, data: savedHomePageState });
        }
      })
      .catch(() => { // Removed e argument
        // console.error('Failed to load homePageState from localForage', e);
      })
      .finally(() => {
        if (activeRef.current) {
          setLoading(false);
        }
      });
  };
  return loadState;
};

const StateWrapper = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const homePageState = useStateValue('homePage');
  const initialMount = useRef(true);
  const activeRef = useRef(true); // Ref to track if component is active

  // Load state from localForage on initial mount
  useEffect(() => {
    const loadState = createLoadState(dispatch, setLoading);

    if (initialMount.current) {
      loadState(activeRef);
      initialMount.current = false;
    }

    return () => {
      activeRef.current = false; // Cleanup to set flag to false
    };
  }, [dispatch, setLoading]); // Dependency array

  // Save state to localForage whenever homePageState changes
  useEffect(() => {
    if (!loading) { // Only save after initial load is complete
      lf.setItem('homePageState', homePageState).catch(() => { // Removed e argument
        // console.error('Failed to save homePageState to localForage', e);
      });
    }
  }, [homePageState, loading]);

  if (loading) {
    return null; // Or a loading spinner
  }

  return children;
};

// Add PropTypes for StateWrapper
StateWrapper.propTypes = {
  children: PropTypes.node.isRequired,
};

const App = () => (
  <CombinedProvider initialState={initialState} reducer={mainReducer}>
    <StateWrapper>
      <Routing />
    </StateWrapper>
  </CombinedProvider>
);
export default memo(App);
